import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, roleLabel, roleBadgeClass } from "@/lib/auth";
import api from "@/lib/api";
import { Search, Bell, LogOut, ChevronDown, Factory, CheckCheck, FileText, Wallet, Calculator, Briefcase, Menu } from "lucide-react";
import { fmtRelative, fmtINR } from "@/lib/format";
import TID from "@/constants/testIds";

const SEARCH_DEBOUNCE_MS = 220;

export default function Header({ onMenuClick = () => {} }) {
    const { user, logout } = useAuth();
    const [menuOpen, setMenuOpen] = useState(false);

    if (!user) return null;
    const initials = (user.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

    return (
        <header className="sticky top-0 z-30 h-16 border-b border-slate-200 glass-header">
            <div className="h-full px-4 sm:px-6 lg:px-10 flex items-center gap-3">
                <button
                    onClick={onMenuClick}
                    className="lg:hidden h-9 w-9 shrink-0 rounded-md border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50"
                    data-testid="header-mobile-menu-btn"
                    aria-label="Open navigation menu"
                >
                    <Menu className="h-4 w-4 text-slate-600" />
                </button>

                {user.role === "PLANT_MANAGER" && (
                    <button
                        data-testid={TID.headerPlantScope}
                        className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-slate-200 bg-white text-[13px] text-slate-700 hover:bg-slate-50"
                    >
                        <Factory className="h-4 w-4 text-slate-500" />
                        <span className="font-medium">My Plant</span>
                        <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                )}

                <GlobalSearch />

                <div className="ml-auto flex items-center gap-2">
                    <NotificationsBell />

                    {/* User pill */}
                    <div className="relative">
                        <button
                            data-testid={TID.headerUserMenu}
                            onClick={() => setMenuOpen((v) => !v)}
                            className="flex items-center gap-2 h-9 pl-1 pr-2 rounded-full border border-slate-200 bg-white hover:bg-slate-50"
                        >
                            <span className="h-7 w-7 rounded-full bg-slate-900 text-white text-[11px] font-semibold flex items-center justify-center">
                                {initials}
                            </span>
                            <div className="leading-tight text-left hidden sm:block">
                                <div className="text-[12px] font-medium text-slate-900">{user.name}</div>
                                <div className={`pill mt-0.5 ${roleBadgeClass(user.role)}`}>
                                    {roleLabel(user.role)}
                                </div>
                            </div>
                            <ChevronDown className="h-3.5 w-3.5 text-slate-400 ml-1" />
                        </button>

                        {menuOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                                <div className="absolute right-0 mt-2 w-64 rounded-lg border border-slate-200 bg-white shadow-lg z-50 overflow-hidden">
                                    <div className="px-4 py-3 border-b border-slate-100">
                                        <div className="text-sm font-medium text-slate-900">{user.name}</div>
                                        <div className="text-[11px] text-slate-500">{user.email}</div>
                                        <div className={`pill mt-2 ${roleBadgeClass(user.role)}`}>
                                            {roleLabel(user.role)}
                                        </div>
                                        {user.title && (
                                            <div className="text-[11px] text-slate-500 mt-2">{user.title}</div>
                                        )}
                                    </div>
                                    <button
                                        data-testid={TID.headerLogout}
                                        onClick={logout}
                                        className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                    >
                                        <LogOut className="h-4 w-4" />
                                        Sign out
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
}

// ============================================================
// Global Search
// ============================================================
function GlobalSearch() {
    const [q, setQ] = useState("");
    const [results, setResults] = useState(null);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const inputRef = useRef(null);

    const timerRef = useRef(null);

    const runSearch = (query) => {
        const trimmed = (query || "").trim();
        if (trimmed.length < 2) { setResults(null); return; }
        api.get(`/search?q=${encodeURIComponent(trimmed)}`)
            .then((r) => { setResults(r.data); setOpen(true); })
            .catch(() => { /* noop */ });
    };

    const onQueryChange = (e) => {
        const v = e.target.value;
        setQ(v);
        if (timerRef.current) clearTimeout(timerRef.current);
        if (v.trim().length < 2) { setResults(null); return; }
        timerRef.current = setTimeout(() => runSearch(v), SEARCH_DEBOUNCE_MS);
    };

    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "k") {
                e.preventDefault();
                inputRef.current?.focus();
            }
            if (e.key === "Escape") {
                setOpen(false); inputRef.current?.blur();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const total = results
        ? (results.parts.length + results.rfqs.length + results.customers.length + results.settlements.length)
        : 0;

    const go = (path) => {
        setOpen(false); setQ(""); setResults(null);
        navigate(path);
    };

    return (
        <div className="flex-1 max-w-[420px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
                ref={inputRef}
                type="text"
                value={q}
                onChange={onQueryChange}
                onFocus={() => results && setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                placeholder="Search parts, RFQs, customers, settlements…"
                className="w-full h-9 pl-9 pr-12 rounded-md border border-slate-200 bg-white text-sm text-slate-700 placeholder:text-slate-400 focus:border-[hsl(var(--copper))] focus:ring-1 focus:ring-[hsl(var(--copper))]/30 focus:outline-none"
                data-testid="header-search-input"
            />
            <kbd className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-0.5 h-5 px-1.5 rounded border border-slate-200 bg-slate-50 text-[10px] font-mono text-slate-500">⌘K</kbd>

            {open && results && (
                <div className="absolute top-full mt-2 left-0 right-0 rounded-lg border border-slate-200 bg-white shadow-lg z-50 max-h-[420px] overflow-auto" data-testid="header-search-dropdown">
                    {total === 0 ? (
                        <div className="p-6 text-center text-[12px] text-slate-400">No results for &ldquo;{q}&rdquo;</div>
                    ) : (
                        <div className="py-2">
                            <SearchGroup label="ZBC Parts" icon={Calculator} items={results.parts} render={(p) => (
                                <div onMouseDown={() => go(`/zbc/${p.id}`)} className="px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`search-part-${p.part_number}`}>
                                    <div className="font-mono text-[12px] font-semibold text-slate-900">{p.part_number}</div>
                                    <div className="text-[11px] text-slate-500">{p.part_name} {p.customer_code && `· ${p.customer_code}`}</div>
                                </div>
                            )} />
                            <SearchGroup label="RFQs" icon={FileText} items={results.rfqs} render={(r) => (
                                <div onMouseDown={() => go(`/rfq/${r.id}`)} className="px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`search-rfq-${r.reference}`}>
                                    <div className="font-mono text-[12px] font-semibold text-slate-900">{r.reference}</div>
                                    <div className="text-[11px] text-slate-500">{r.customer_code} · {fmtINR(r.total_price || 0)} · {r.status?.replace(/_/g, " ")}</div>
                                </div>
                            )} />
                            <SearchGroup label="Customers" icon={Briefcase} items={results.customers} render={(c) => (
                                <div onMouseDown={() => go(`/foundation/customers`)} className="px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`search-cust-${c.code}`}>
                                    <div className="font-mono text-[12px] font-semibold text-slate-900">{c.code}</div>
                                    <div className="text-[11px] text-slate-500">{c.name} · {c.formula_variant}</div>
                                </div>
                            )} />
                            <SearchGroup label="Settlements" icon={Wallet} items={results.settlements} render={(s) => (
                                <div onMouseDown={() => go(`/settlement/${s.id}`)} className="px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`search-sett-${s.reference}`}>
                                    <div className="font-mono text-[12px] font-semibold text-slate-900">{s.reference}</div>
                                    <div className="text-[11px] text-slate-500">{s.customer_code} · {s.status?.replace(/_/g, " ")}</div>
                                </div>
                            )} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function SearchGroup({ label, icon: Icon, items, render }) {
    if (!items || items.length === 0) return null;
    return (
        <div className="mb-1">
            <div className="px-3 py-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50/60">
                <Icon className="h-3 w-3" /> {label} <span className="text-slate-400">({items.length})</span>
            </div>
            {items.map((it, i) => <div key={i}>{render(it)}</div>)}
        </div>
    );
}

// ============================================================
// Notifications Bell
// ============================================================
function NotificationsBell() {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState({ items: [], unread_count: 0 });
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    const load = () => {
        api.get("/notifications?limit=20")
            .then((r) => setData(r.data))
            .catch(() => { /* noop */ });
    };
    useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

    const markAll = () => {
        setBusy(true);
        api.post("/notifications/read-all")
            .then(load)
            .finally(() => setBusy(false));
    };
    const onItemClick = (n) => {
        const finish = () => { setOpen(false); load(); if (n.link) navigate(n.link); };
        if (!n.is_read) {
            api.post(`/notifications/${n.id}/read`).then(finish).catch(finish);
        } else {
            finish();
        }
    };

    return (
        <div className="relative">
            <button
                onClick={() => setOpen((v) => !v)}
                className="relative h-9 w-9 rounded-md border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50"
                data-testid="header-notifications-btn"
            >
                <Bell className="h-4 w-4 text-slate-600" />
                {data.unread_count > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-[hsl(var(--copper))] text-white text-[9px] font-bold flex items-center justify-center" data-testid="header-notifications-badge">
                        {data.unread_count > 9 ? "9+" : data.unread_count}
                    </span>
                )}
            </button>

            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 mt-2 w-96 rounded-lg border border-slate-200 bg-white shadow-lg z-50 max-h-[480px] overflow-hidden flex flex-col" data-testid="header-notifications-dropdown">
                        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between bg-slate-50/60">
                            <div className="font-display text-[13px] font-semibold text-slate-900">Notifications</div>
                            {data.unread_count > 0 && (
                                <button onClick={markAll} disabled={busy} className="text-[11px] text-[hsl(var(--copper))] hover:underline flex items-center gap-1" data-testid="notif-mark-all-read">
                                    <CheckCheck className="h-3 w-3" /> Mark all read
                                </button>
                            )}
                        </div>
                        <div className="overflow-auto flex-1">
                            {data.items.length === 0 ? (
                                <div className="p-8 text-center text-[12px] text-slate-400">You&rsquo;re all caught up.</div>
                            ) : (
                                <ul>
                                    {data.items.map((n) => (
                                        <li
                                            key={n.id}
                                            onClick={() => onItemClick(n)}
                                            className={`px-4 py-3 border-b border-slate-50 cursor-pointer hover:bg-slate-50 ${!n.is_read ? "bg-[hsl(var(--copper))]/5" : ""}`}
                                            data-testid={`notif-item-${n.id}`}
                                        >
                                            <div className="flex items-start gap-2">
                                                {!n.is_read && <div className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--copper))] mt-1.5 shrink-0" />}
                                                <div className={`flex-1 ${n.is_read ? "pl-3.5" : ""}`}>
                                                    <div className="text-[12.5px] font-medium text-slate-900 leading-snug">{n.title}</div>
                                                    {n.body && <div className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">{n.body}</div>}
                                                    <div className="text-[10px] text-slate-400 mt-1 font-mono">{fmtRelative(n.created_at)}</div>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
